import { useEffect, useMemo, useState, type ReactNode, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, ButtonToggle } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import YearPicker from '../../../components/picker/YearPicker';
import WheelPicker from '../../../components/picker/WheelPicker';
import { BUDDY_KEYWORDS, REGION_DATA } from '../constants/buddyKeywords';
import type { BuddyKeywords, FanBuddySettingStep } from '../FanBuddySettingPage';
import { useStepAutoScroll } from '../hooks/useStepAutoScroll';

const AGE_GAP_MAX = 12;
const GENDER_OPTIONS = ['female', 'male', 'other'];

interface SelectBuddyKeywordsProps {
  handleSettingStep: (step: FanBuddySettingStep) => void;
  buddyKeywords: BuddyKeywords;
  onUpdateBuddyKeywords: (buddyKeywords: Partial<BuddyKeywords>) => void;
  onBackToFanStyleLastStep: () => void;
  forceShowAllSteps?: boolean;
}

const inferActiveStepFromBuddyKeywords = (
  buddyKeywords: BuddyKeywords,
  forceShowAllSteps: boolean
) => {
  if (forceShowAllSteps) return 3;
  if (buddyKeywords.gender !== undefined) return 3;
  if (buddyKeywords.birthYear !== undefined) return 2;
  return 0;
};

const SelectBuddyKeywords = ({
  handleSettingStep,
  buddyKeywords,
  onUpdateBuddyKeywords,
  onBackToFanStyleLastStep,
  forceShowAllSteps = false
}: SelectBuddyKeywordsProps) => {
  const { t } = useTranslation();
  const inferredActiveStep = useMemo(
    () => inferActiveStepFromBuddyKeywords(buddyKeywords, forceShowAllSteps),
    [forceShowAllSteps, buddyKeywords]
  );

  const [activeStep, setActiveStep] = useState(() => inferredActiveStep);
  const [hasStartedSteps, setHasStartedSteps] = useState(
    () => forceShowAllSteps || buddyKeywords.keywords.length > 0
  );
  const { requestScrollToStep, stepRefs } = useStepAutoScroll(activeStep, { offset: 24 });

  // 뒤로갔다가 다시 들어왔을 때, 이미 선택된 값이 있으면 UI가 접히지 않도록 복원
  useEffect(() => {
    setHasStartedSteps(forceShowAllSteps || buddyKeywords.keywords.length > 0);
  }, [forceShowAllSteps, buddyKeywords.keywords.length]);

  useEffect(() => {
    setActiveStep((prev) => Math.max(prev, inferredActiveStep));
  }, [inferredActiveStep]);

  const handleChange = (key: keyof BuddyKeywords, value: BuddyKeywords[keyof BuddyKeywords]) => {
    onUpdateBuddyKeywords({ [key]: value } as Partial<BuddyKeywords>);
  };

  const handleItemClick = (item: string) => {
    const isRemoving = buddyKeywords.keywords.includes(item);
    const nextKeywords = isRemoving
      ? buddyKeywords.keywords.filter((x: string) => x !== item)
      : ([...buddyKeywords.keywords, item] as string[]);

    setHasStartedSteps(forceShowAllSteps || nextKeywords.length > 0);
    onUpdateBuddyKeywords({ keywords: nextKeywords });
  };

  const canSubmit = useMemo(() => {
    const hasKeyword = buddyKeywords.keywords.length > 0;
    const hasGender = buddyKeywords.gender !== null;
    const hasDistrict = buddyKeywords.locationSigungu.trim().length > 0;

    return hasKeyword && hasGender && hasDistrict;
  }, [buddyKeywords]);

  const isButtonDisable = !canSubmit;

  return (
    <div className="min-h-screen pb-[120px] pt-[env(safe-area-inset-top)]">
      <Header handlePreviousStep={onBackToFanStyleLastStep} />
      <div className="px-[24px]">
        <h1 className="text-[26px] font-bold leading-[31px] text-text-main-lv1 my-[10px]">
          {t('FanBuddySetting.buddyKeywordsTitle')}
        </h1>
        <div className="mt-[23px] flex flex-wrap gap-[10px]">
          {BUDDY_KEYWORDS.map((item) => (
            <KeywordTag
              key={item}
              item={item}
              isSelected={buddyKeywords.keywords.includes(item)}
              onClick={() => handleItemClick(item)}
            />
          ))}
        </div>
        {hasStartedSteps && (
          <AnimatePresence initial={false}>
            {activeStep >= 0 && (
              <StepContainer key="step-age-gap" stepKey="step-age-gap">
                <SelectAgeGapSection
                  selectedKeywords={buddyKeywords}
                  handleChange={(key, value) => {
                    const prev = buddyKeywords[key];
                    handleChange(key, value);
                    if (key === 'allowAgeGap' && prev !== value) {
                      setActiveStep((s) => Math.max(s, 1));
                      requestScrollToStep('step-age');
                    }
                  }}
                />
              </StepContainer>
            )}

            {activeStep >= 1 && (
              <StepContainer key="step-age" stepKey="step-age" containerRef={stepRefs.stepAgeRef}>
                <SelectAgeSection
                  selectedKeywords={buddyKeywords}
                  handleChange={(key, value) => {
                    const prev = buddyKeywords[key];
                    handleChange(key, value);
                    if (key === 'birthYear' && prev !== value) {
                      setActiveStep((s) => Math.max(s, 2));
                      requestScrollToStep('step-gender');
                    }
                  }}
                />
              </StepContainer>
            )}

            {activeStep >= 2 && (
              <StepContainer
                key="step-gender"
                stepKey="step-gender"
                containerRef={stepRefs.stepGenderRef}
              >
                <SelectGenderSection
                  selectedKeywords={buddyKeywords}
                  handleChange={(key, value) => {
                    handleChange(key, value);
                    if (key === 'gender') {
                      setActiveStep((s) => Math.max(s, 3));
                      requestScrollToStep('step-region');
                    }
                  }}
                />
              </StepContainer>
            )}

            {activeStep >= 3 && (
              <StepContainer
                key="step-region"
                stepKey="step-region"
                containerRef={stepRefs.stepRegionRef}
              >
                <SelectRegionSection selectedKeywords={buddyKeywords} handleChange={handleChange} />
              </StepContainer>
            )}
          </AnimatePresence>
        )}
      </div>
      <div className="fixed bottom-0 left-0 right-0 px-[24px] w-full bg-background-light dark:bg-background-dark pt-[10px] pb-[calc(10px+env(safe-area-inset-bottom))]">
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          disabled={isButtonDisable}
          onClick={() => handleSettingStep('register-sns-account')}
        >
          {t('next')}
        </Button>
      </div>
    </div>
  );
};

export default SelectBuddyKeywords;

const Header = ({ handlePreviousStep }: { handlePreviousStep: () => void }) => {
  const { t } = useTranslation();
  return (
    <header className="px-[16px] py-[12px] flex items-center justify-center relative w-full">
      <button
        type="button"
        className="absolute left-[16px] top-[50%] translate-y-[-50%]"
        onClick={handlePreviousStep}
      >
        <ArrowLeft size={24} className="text-text-main-lv1" />
      </button>
      <p className="text-text-main-lv1 text-[17px] font-bold leading-[20px]">
        {t('FanBuddySetting.title')}
      </p>
    </header>
  );
};

const KeywordTag = ({
  item,
  isSelected,
  onClick
}: {
  item: string;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={`rounded-[1px] px-[2px] w-fit border ${isSelected ? 'bg-text-main-lv1 dark:bg-[#FAFAFA] border-text-main-lv1' : 'border-separator2 '}`}
      onClick={onClick}
    >
      <p
        className={`text-[20px] font-semibold leading-[24px] ${isSelected ? 'text-pure-white dark:text-pure-black' : 'text-text-main-lv3'}`}
      >
        {t(`ChatPage.fanBuddy.keywords.${item}`)}
      </p>
    </button>
  );
};

const StepContainer = ({
  children,
  stepKey,
  containerRef
}: {
  children: ReactNode;
  stepKey: string;
  containerRef?: Ref<HTMLDivElement>;
}) => {
  return (
    <motion.div
      key={stepKey}
      ref={containerRef}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.2 } }}
      exit={{ opacity: 0, y: -12, transition: { duration: 0.15 } }}
    >
      {children}
    </motion.div>
  );
};

const SelectAgeGapSection = ({
  selectedKeywords,
  handleChange
}: {
  selectedKeywords: BuddyKeywords;
  handleChange: (key: keyof BuddyKeywords, value: BuddyKeywords[keyof BuddyKeywords]) => void;
}) => {
  const { t, i18n } = useTranslation();
  const isEnLocale = i18n.language?.startsWith('en') ?? false;
  const ageGapValue = selectedKeywords.allowAgeGap ?? 0;
  const ageGapPercent = Math.min((ageGapValue / AGE_GAP_MAX) * 100, 100);
  const unchecked = selectedKeywords.allowAgeGap === undefined;

  return (
    <div>
      <p className="text-[17px] font-semibold leading-[20px] text-text-main-lv1 pt-[32px] pb-[16px]">
        {t('FanBuddySetting.howOldAreYouOKWith')}
      </p>
      {selectedKeywords.allowAgeGap !== undefined && (
        <div className="py-[8px] flex items-center gap-[20px]">
          <p className="text-[17px] font-semibold leading-[20px] text-text-main-lv3 whitespace-nowrap">
            {selectedKeywords.allowAgeGap === 0 || selectedKeywords.allowAgeGap === undefined
              ? t('FanBuddySetting.sameAge')
              : `${isEnLocale ? '±' : ''} ${selectedKeywords.allowAgeGap}${isEnLocale ? '' : '살'}`}
          </p>
          <div className="relative w-full h-[24px]">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[24px] rounded-full bg-surface-lv2" />
            <div
              className="absolute left-0 top-1/2 -translate-y-1/2 h-[24px] rounded-full bg-surface-lv2 pointer-events-none"
              style={{
                width: unchecked ? '0%' : `${ageGapPercent}%`
              }}
            />
            <input
              type="range"
              min={0}
              max={AGE_GAP_MAX}
              value={ageGapValue}
              onChange={(e) => handleChange('allowAgeGap', Number(e.target.value))}
              className="absolute inset-0 w-full h-full opacity-0 p-0 m-0"
              style={{
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                cursor: unchecked ? 'not-allowed' : 'pointer'
              }}
              aria-label="age-gap-range"
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full bg-pure-white border-[1.5px] border-placeholder-light w-[24px] h-[24px] pointer-events-none"
              style={{
                left: `${ageGapPercent}%`
              }}
            />
          </div>
        </div>
      )}
      <div className="flex items-center gap-[8px] py-[10px]">
        <ButtonToggle
          type="check"
          size="lg"
          checked={unchecked}
          onChange={(checked) => handleChange('allowAgeGap', checked ? undefined : 0)}
        />
        <p className="text-[15px] text-text-main-lv1">{t('FanBuddySetting.noMatter')}</p>
      </div>
    </div>
  );
};

const SelectAgeSection = ({
  selectedKeywords,
  handleChange
}: {
  selectedKeywords: BuddyKeywords;
  handleChange: (key: keyof BuddyKeywords, value: BuddyKeywords[keyof BuddyKeywords]) => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-[19px]">
      <p className="text-[17px] font-semibold leading-[20px] text-text-main-lv1 pt-[32px] pb-[16px]">
        {t('FanBuddySetting.howOldAreYou')}
      </p>
      <YearPicker
        initYear={selectedKeywords.birthYear}
        minYear={1970}
        maxYear={new Date().getFullYear() - 10}
        onSelectedYear={(year) => handleChange('birthYear', Number(year))}
        placeholder={t('select') as string}
        postfix={t('FanBuddySetting.yearOfBirth') as string}
      />
    </div>
  );
};

const SelectGenderSection = ({
  selectedKeywords,
  handleChange
}: {
  selectedKeywords: BuddyKeywords;
  handleChange: (key: keyof BuddyKeywords, value: BuddyKeywords[keyof BuddyKeywords]) => void;
}) => {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-[19px]">
      <p className="text-[17px] font-semibold leading-[20px] text-text-main-lv1 pt-[32px] pb-[16px]">
        {t('FanBuddySetting.selectGender')}
      </p>
      <div className="flex items-center gap-[6px]">
        {GENDER_OPTIONS.map((gender) => (
          <button
            key={gender}
            type="button"
            onClick={() => handleChange('gender', gender)}
            className={`py-[16px] rounded-[8px] text-[17px] font-semibold leading-[20px] w-full border ${
              selectedKeywords.gender === gender
                ? 'bg-label-light dark:bg-label-dark border-text-main-lv2 text-text-main-lv2'
                : 'text-text-main-lv2 border-separator1'
            }`}
          >
            {t(`FanBuddySetting.gender.${gender}`)}
          </button>
        ))}
      </div>
      <ul className="text-[12px] leading-[14px] text-text-sub-lv1 list-disc font-medium mt-[-9px] list-inside">
        {selectedKeywords.gender === 'female' && (
          <li>{t('FanBuddySetting.femaleRecommendDescription')}</li>
        )}
        {selectedKeywords.gender === 'male' && (
          <li>{t('FanBuddySetting.maleRecommendDescription')}</li>
        )}
      </ul>
    </section>
  );
};

const SelectRegionSection = ({
  selectedKeywords,
  handleChange
}: {
  selectedKeywords: BuddyKeywords;
  handleChange: (key: keyof BuddyKeywords, value: BuddyKeywords[keyof BuddyKeywords]) => void;
}) => {
  const { t } = useTranslation();
  const regions = useMemo(() => Object.keys(REGION_DATA), []);
  const districts = REGION_DATA[selectedKeywords.locationSido] ?? [];

  return (
    <section className="flex flex-col gap-[16px]">
      <p className="text-[17px] font-semibold leading-[20px] text-text-main-lv1 pt-[32px] pb-[16px]">
        {t('FanBuddySetting.selectRegion')}
      </p>
      <div className="overflow-hidden">
        <div className="relative px-[20px]">
          <div className="relative">
            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none rounded-[8px] bg-surface-lv2 h-[32px]" />

            <div className="flex items-stretch">
              <WheelPicker
                ariaLabel="region-picker"
                className="flex-1"
                items={regions}
                value={selectedKeywords.locationSido}
                onChange={(region) => {
                  const nextDistricts = REGION_DATA[region] ?? [];
                  handleChange('locationSido', region);
                  handleChange('locationSigungu', nextDistricts[0] ?? '');
                }}
              />
              <WheelPicker
                ariaLabel="district-picker"
                className="flex-1"
                items={districts}
                value={selectedKeywords.locationSigungu}
                onChange={(district) => handleChange('locationSigungu', district)}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
